import React, { ReactNode } from 'react'
import clsx from 'clsx'
import { Rating as RatingStyles } from '@airbus/elements/elements'
import { StarBorder, StarHalf, Star } from '@mui/icons-material'
import { createUseStyles } from '../../lib/styling'
import useControlled from '../../lib/hooks/useControlled'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'
import Tooltip from '../Tooltip'

type Tuple<TItem, TLength extends number> = [TItem, ...TItem[]] & { length: TLength }

type Props = {
  /**
   * general label
   * @default x out of
   */
  ariaLabel?: string
  /** default rating value when uncontrolled (from 0 to 5) */
  defaultValue?: number
  /** disable the whole component */
  disabled?: boolean
  /**
   * to customize items labels
   * Displayed as tooltip and for screen readers
   * @default "x of 5" */
  getItemLabel?: (index: number) => string
  /** callback when rating changes */
  onChange?: (value: number) => void
  /** static mode */
  readOnly?: boolean
  /** to customize items */
  renderItem?: (props: RatingItemProps) => ReactNode
  /**
   * medium | small
   * @default medium
   */
  size?: 'medium' | 'small'
  /** Array of tooltips for each star */
  tooltips?: Tuple<string, 5>
  /** current rating value (from 0 to 5) */
  value?: number
}

type DeprecatedProps = {
  rate?: number
}

export type RatingProps = Props & DeprecatedProps & CommonProps

type RatingItemProps = {
  index: number
  isChecked: boolean
  isHalf: boolean
  isFull: boolean
}

const useStyles = createUseStyles(RatingStyles, 'rating')
const useTooltipStyles = createUseStyles(
  () => ({
    root: {
      display: 'inline-flex',
      justifyContent: 'center',
    },
  }),
  'rating-tooltip',
)

const handleDeprecatedProps = (props: RatingProps) => {
  const { rate, value, ...rest } = props
  return {
    value: rate && rate >= 0 ? rate / 20 : value,
    ...rest,
  }
}

const iterator = Array.from(Array(5).keys())

const WithOptionalTooltips = (props: { children: ReactNode; label?: string; size?: 'medium' | 'small' }) => {
  const { label, children, size = 'medium' } = props
  const classes = useTooltipStyles()

  return label ? (
    <Tooltip placement='top' offset={size === 'small' ? [0, 16] : [0, 24]} label={label} targetClassName={classes.root}>
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  )
}

const Rating = (props: RatingProps) => {
  const {
    ariaLabel,
    className,
    defaultValue = 0,
    disabled,
    getItemLabel,
    onChange,
    readOnly,
    renderItem,
    size = 'medium',
    tooltips = [],
    value: valueProp,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('Rating', { rate: 'value' }, props)
  const classes = useStyles()
  const localRef = React.useRef<HTMLDivElement>(null)

  // controlled / uncontrolled value
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue: defaultValue,
  })

  const updateValue = React.useCallback(
    (newValue: number) => {
      setValue(newValue)
      if (onChange) {
        onChange(newValue)
      }
    },
    [onChange, setValue],
  )

  const getItems = React.useCallback((): HTMLButtonElement[] => {
    if (!localRef.current) return []
    return Array.from(localRef.current.querySelectorAll("button[class*='item']"))
  }, [])

  const handleClick = React.useCallback(
    (itemValue: number, isFull: boolean, isChecked: boolean) => {
      if (readOnly) return
      let newValue = itemValue
      if (isChecked && isFull) {
        newValue = 0
      }
      updateValue(newValue)
    },
    [readOnly, updateValue],
  )

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const managedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
      const { key } = event.nativeEvent
      if (!managedKeys.includes(key)) return
      if (!localRef.current) return
      const focusedItem = document.activeElement
      if (!focusedItem) return
      const isRatingItem = focusedItem.className.indexOf('-item') > 0 && focusedItem.getAttribute('role') === 'radio'
      if (!isRatingItem) return
      event.preventDefault()

      let newValue = Math.round(value)
      switch (key) {
        default:
        case 'ArrowDown':
        case 'ArrowLeft':
          if (newValue > 0) newValue--
          break
        case 'ArrowRight':
        case 'ArrowUp':
          if (newValue < 5) newValue++
          break
      }
      if (newValue === value) return

      const newItem = getItems()[newValue > 0 ? newValue - 1 : newValue]
      updateValue(newValue)
      setTimeout(() => newItem.focus())
    },
    [getItems, updateValue, value],
  )

  return (
    <div
      className={clsx(classes.root, className, {
        [classes._small]: size === 'small',
        [classes._readonly]: readOnly,
      })}
      aria-label={ariaLabel || `${value} out of 5`}
      aria-disabled={disabled}
      onKeyDownCapture={handleKeyDown}
      ref={localRef}
      role='radiogroup'
      {...rest}
    >
      {iterator.map((key) => {
        const itemValue = key + 1
        const isHalf = value > key && value < itemValue
        const isFull = value >= itemValue
        const isChecked = isHalf || value === itemValue
        const isFocusable = isChecked || (value === 0 && key === 0)
        return (
          <button
            type='button'
            key={`item-${key}`}
            className={clsx(classes.item, {
              [classes.item_half]: isHalf,
              [classes.item_full]: isFull,
            })}
            role='radio'
            aria-checked={isChecked ? 'true' : 'false'}
            aria-label={getItemLabel ? getItemLabel(key) : `${itemValue} of 5`}
            disabled={disabled}
            onClick={() => handleClick(itemValue, isFull, isChecked)}
            tabIndex={isFocusable ? 0 : -1}
          >
            <WithOptionalTooltips label={tooltips[key]} size={size}>
              {renderItem ? (
                renderItem({ index: key, isChecked, isHalf, isFull })
              ) : (
                <>
                  <StarBorder className={classes.icon} />
                  <StarHalf className={clsx(classes.icon, classes.icon_half)} />
                  <Star className={clsx(classes.icon, classes.icon_full)} />
                </>
              )}
            </WithOptionalTooltips>
          </button>
        )
      })}
    </div>
  )
}

export default Rating
